import { AfterContentChecked, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MaskType } from 'src/app/constants/masks';
import { FormValidationService } from 'src/app/services/form-validation.service';
import { MaskService } from 'src/app/services/mask.service';
import { CoveragePayload, CoverageTab } from 'src/app/models/coverage';
import { TaxStaus } from 'src/app/constants/tax-status';
import { HelperService } from 'src/app/services/helper.service';
import { EnrollmentManagerService } from 'src/app/services/enrollment-manager.service';
import { BenefitWizardType } from 'src/app/constants/wizard-panels';
import { BooleanEnum } from 'src/app/constants/boolean-enum';
import { DependentDialogModel } from 'src/app/models/dialog';
import { MatDialog } from '@angular/material/dialog';
import { DialogDependentComponent } from 'src/app/components/shared/dialog/dependent.component';
import { NextPanel } from 'src/app/models/next-panel';
import { ToastService } from 'src/app/services/toast.service';
import { ToastType } from 'src/app/constants/toast-type';

/** FORM GROUP STRUCTURE
 * OLD MwP:  hg-inner-wizard
 * OLD MwP:  hg-bu-inner-wizard
 * 
 * Base model is passed in with no validation
 * Each sub component will independently add controls, validation, and form arrays when needed.
 * After all sub components build, angular needs to alter parent of changes that is done in ngAfterContentChecked.
 * 
 * NOTE: If we do not do dectect changes after content checked will cause errors in the FormGroup
 *                              |
 * hg-inner-wizard              | hg-bu-inner-wizard            | Complete  | Note
 * ===============================================================================
 *                              | currentCoverage               | X
 *                              | currentRate                   | X
 * showActiveAtWork             | showActiveAtWork              | X
 * showYesKnockout              | showYesKnockout               | X
 * showPayrollTaxStatus         | showPayrollTaxStatus          | X
 * showEnrollmentReason         | showEnrollmentReason          | X
 * showFlatTier                 | showFlatTier                  | X
 * showManualCoverage           | showManualCoverage            | x
 * showCoveredMember            | showCoveredMember             | x
 * showCoverageDropdown         | showCoverageDropdown          | x
 * showFsaOption                | showFsaOption                 | X
 * showHsaOption                | showHsaOption                 | x
 * editFsaAnnualF               | editFsaAnnualF                |           | This screen is not configured in the API and does nothing in the OlD MWP App
 * showManualRate               | showManualRate                | x
 * showMiscDeductionCoverage    | showMiscDeductionCoverage     | x
 * showCalcedTierSelectionGrid  | showCalcedTierSelectionGrid   | x
 * showAddDependent             | showAddDependent              | x
 */

@Component({
    selector: 'mwp-benefit-enrollment-wizard-coverage',
    templateUrl: './coverage.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class CoverageComponent implements OnInit, AfterContentChecked {
    @Input() public coverageTab: CoverageTab;
    @Output() public emitCancel: EventEmitter<any> = new EventEmitter();
    @Output() public emitComplete: EventEmitter<CoveragePayload> = new EventEmitter();
    @Output() public emitReload: EventEmitter<any> = new EventEmitter();

    public maskType = MaskType;
    public taxStatus = TaxStaus;
    public booleanEnum = BooleanEnum;

    public isActiveAtWorkName: string = 'isActiveAtWork';
    public isYesKnockoutName: string = 'isYesKnockout';
    public calcedTierGroupName: string = 'coveredMembers';
    public manualCoverageValueName: string = 'manualCoverageValue';
    public coverageDropdownValueName: string = 'coverageDropdownValue';
    public manualRateValueName: string = 'manualRateValue';
    public taxStatusQuestion: string = 'Payroll Tax Status';
    public isPretaxStatusName: string = 'isPretaxStatus';
    public changingTaxStatusOnlyName: string = 'changingTaxStatusOnly';
    public miscDedCoverageValueName: string = 'miscDedCoverageValue';
    public displayChangingTaxStatusOnly: boolean;
    public coverageFormGroup: FormGroup;
    public panelName: string;
    public isNotConfigured: boolean;
    public displayGenericBenefitOptions: boolean;
    public displayAddDependentButton: boolean;
    public coverageBenefitName: string;

    constructor(
        private fb: FormBuilder,
        private cd: ChangeDetectorRef,
        private maskService: MaskService,
        private helperService: HelperService,
        private enrollmentManagerService: EnrollmentManagerService,
        private toastService: ToastService,
        private dialog: MatDialog,
    ) {
    }

    ngOnInit(): void {
        this.coverageFormGroup = this.fb.group(new CoveragePayload());
        if (this.coverageTab.showActiveAtWork) {
            this.coverageFormGroup.get(this.isActiveAtWorkName).setValue(null);
            this.coverageFormGroup.get(this.isActiveAtWorkName).setValidators(Validators.required);
        } else if (this.coverageTab.showYesKnockout) {
            this.coverageFormGroup.get(this.isYesKnockoutName).setValue(null);
            this.coverageFormGroup.get(this.isYesKnockoutName).setValidators(Validators.required);
        } else if (this.coverageTab.showManualCoverage) {
            this.coverageFormGroup.get(this.manualCoverageValueName).setValue(null);
            this.coverageFormGroup.get(this.manualCoverageValueName).setValidators([Validators.required, Validators.compose([FormValidationService.validCurrency(true)])]);
        } else if (this.coverageTab.showCoverageDropdown) {
            this.coverageFormGroup.get(this.coverageDropdownValueName).setValue(null);
            this.coverageFormGroup.get(this.coverageDropdownValueName).setValidators([Validators.required, Validators.compose([FormValidationService.validnumber(true)])]);
        } else if (this.coverageTab.showManualRate) {
            this.coverageFormGroup.get(this.manualRateValueName).setValue(null);
            this.coverageFormGroup.get(this.manualRateValueName).setValidators([Validators.required, Validators.compose([FormValidationService.validCurrency(true)])]);
        } else if (this.coverageTab.showPayrollTaxStatus) {
            this.coverageFormGroup.get(this.isPretaxStatusName).setValue(null);
            this.coverageFormGroup.get(this.isPretaxStatusName).setValidators(Validators.required);
            if (this.enrollmentManagerService.benefitWizardControlPanel.wizardType == BenefitWizardType.BuyUp) {
                this.coverageFormGroup.get(this.changingTaxStatusOnlyName).setValue(false);
                this.displayChangingTaxStatusOnly = true;
            }
        } else if (this.coverageTab.showMiscDeductionCoverage) {
            this.coverageFormGroup.get(this.miscDedCoverageValueName).setValue(null);
            this.coverageFormGroup.get(this.miscDedCoverageValueName).setValidators([Validators.required, Validators.compose([FormValidationService.validCurrency(true)])]);
        }

        if (this.coverageTab.showAddDependent && !this.coverageTab.showCalcedTierSelectionGrid) {
            this.displayAddDependentButton = true;
        }

        this.displayGenericBenefitOptions = !this.helperService.isEmpty(this.coverageTab.currentCoverage) ||
            !this.helperService.isEmpty(this.coverageTab.currentRate) ||
            this.coverageTab.showActiveAtWork ||
            this.coverageTab.showYesKnockout ||
            this.coverageTab.showPayrollTaxStatus ||
            this.coverageTab.showEnrollmentReason ||
            this.coverageTab.showFlatTier ||
            this.coverageTab.showManualCoverage ||
            this.coverageTab.showCoveredMember ||
            this.coverageTab.showCoverageDropdown ||
            this.coverageTab.showManualRate ||
            this.coverageTab.showMiscDeductionCoverage;
        this.coverageBenefitName = this.coverageTab.coverageTabName ? this.coverageTab.coverageTabName : this.enrollmentManagerService.manager.selectedBenefitName;

        if (this.panelName) {
            this.panelName = `Coverage - ${this.panelName}`;
            this.isNotConfigured = true;
        }
    }

    ngAfterContentChecked(): void {
        this.cd.detectChanges();
    }

    public submitCoverage(): void {
        const l_CoveragePayload = this.helperService.convertFormGroupIntoObject(this.coverageFormGroup, new CoveragePayload());
        if (this.coverageTab.showManualCoverage && l_CoveragePayload.manualCoverageValue)
            l_CoveragePayload.manualCoverageValue = this.maskService.removeCurrencyMask(l_CoveragePayload.manualCoverageValue);

        if (this.coverageTab.showManualRate && l_CoveragePayload.manualRateValue)
            l_CoveragePayload.manualRateValue = this.maskService.removeCurrencyMask(l_CoveragePayload.manualRateValue);

        if (this.coverageTab.showHsaOption && l_CoveragePayload.hsaContribution)
            l_CoveragePayload.hsaContribution = this.maskService.removeCurrencyMask(l_CoveragePayload.hsaContribution);

        if (this.coverageTab.showFsaOption && l_CoveragePayload.fsaAnnualContribution)
            l_CoveragePayload.fsaAnnualContribution = this.maskService.removeCurrencyMask(l_CoveragePayload.fsaAnnualContribution);

        if (this.coverageTab.showMiscDeductionCoverage && l_CoveragePayload.miscDedCoverageValue) {
            const l_FormattedDeductionValue = l_CoveragePayload.miscDedCoverageValue;
            l_CoveragePayload.miscDedCoverageValue = this.maskService.removeCurrencyMask(l_CoveragePayload.miscDedCoverageValue);

            if (this.coverageTab?.miscDeductionCoverageMaxValue > 0 && l_CoveragePayload.miscDedCoverageValue > this.coverageTab.miscDeductionCoverageMaxValue) {
                this.toastService.buildToastMessage(ToastType.warning, 'Deduction Error!', `Amount of ${l_FormattedDeductionValue} must be less than or equal to $${this.coverageTab.miscDeductionCoverageMaxValue}`);
                return;
            }
        }

        this.emitComplete.emit(l_CoveragePayload);
    }

    public openDependentDialog(): void {
        const l_DependentDialogModel = new DependentDialogModel();
        l_DependentDialogModel.isWizard = true;
        l_DependentDialogModel.wizardType = this.enrollmentManagerService.benefitWizardControlPanel.wizardType;
        const dialogRef = this.dialog.open(DialogDependentComponent, { data: l_DependentDialogModel, panelClass: 'mwp-modal-override-600' });

        dialogRef.afterClosed().subscribe((response: NextPanel) => {
            if (response) {
                this.reload();
            }
        });
    }

    public reloadTierGrid(value: number): void {
        if (this.coverageTab.showCalcedTierSelectionGrid)
            this.enrollmentManagerService.$ReloadCoverageTierGirdByDetailId.next(value);
    }

    public cancel(): void {
        this.emitCancel.emit();
    }

    public reload(): void {
        this.emitReload.emit();
    }
}